import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectIsAuthenticated } from "../authentication/authenticationSlice";
import { recordSignUpArrival, reportMetaClick } from "./slice-extensions/attribution-slice-extension";
import {
  captureMetaClickFromUrl,
  clearPendingMetaClick,
  getPendingMetaClick,
} from "./metaClickCapture";
import {
  markSignUpArrivalReported,
  readSignUpArrival,
  wasSignUpArrivalReported,
} from "./signUpArrivalCapture";

/**
 * Invisible paid-acquisition plumbing: counts an arrival from a marketing-site
 * CTA (anonymous, once per tab), stashes an inbound ?fbclid= on any route so
 * it survives until sign-up (localStorage, 7 days), and right after
 * authentication hands it to the backend, which reports the trial start to
 * Meta server-side. Silent by design — nothing here is ever shown to the user.
 */
const AttributionGate = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const reportAttempted = useRef(false);

  useEffect(() => {
    captureMetaClickFromUrl(location.search);
  }, [location.search]);

  useEffect(() => {
    const arrival = readSignUpArrival(location.search);
    if (!arrival || wasSignUpArrivalReported(location.search)) {
      return;
    }
    markSignUpArrivalReported(location.search);
    dispatch(recordSignUpArrival(arrival));
  }, [location.search, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || reportAttempted.current) {
      return;
    }
    const clickId = getPendingMetaClick();
    if (!clickId) {
      return;
    }
    reportAttempted.current = true;
    dispatch(reportMetaClick({ clickId }))
      .unwrap()
      .then(() => clearPendingMetaClick())
      .catch((error: { statusCode?: number }) => {
        if (error.statusCode && error.statusCode < 500) {
          // Rejected for good (bad click id, no instructor) — never retry it.
          clearPendingMetaClick();
          return;
        }
        // Network hiccup: keep the click stashed; the next session retries.
        reportAttempted.current = false;
      });
  }, [isAuthenticated, dispatch]);

  return null;
};

export default AttributionGate;
